import {
  ActionIcon,
  Anchor,
  Divider,
  Popover,
  Radio,
  SegmentedControl,
  Stack,
  Tooltip,
} from '@mantine/core';
import { PiSortAscending, PiSortDescending } from 'react-icons/pi';
import { CandidateStage } from '@company/types/job-overview.type';
import {
  defaultCandidateFilters,
  useJobDetailStore,
  useActiveCandidateFilters,
} from '@company/pages/JobOverview/DetailV2/stores/jobDetailStore';

// Sort fields the BE supports per tab; only Applicants is sortable today.
const TAB_SORT_FIELDS: Partial<
  Record<CandidateStage, { value: string; label: string }[]>
> = {
  [CandidateStage.APPLICANTS]: [
    { value: 'rating', label: 'Rating' },
    { value: 'dateApplied', label: 'Date applied' },
  ],
};

export default function SortControl() {
  const activeTab = useJobDetailStore((state) => state.activeTab);
  const candidateFilters = useActiveCandidateFilters();
  const setCandidateFilters = useJobDetailStore(
    (state) => state.setCandidateFilters
  );

  const fields = TAB_SORT_FIELDS[activeTab] ?? [];
  const { sortBy, order } = candidateFilters;
  const isAsc = order !== 'desc';

  // This tab doesn't support sorting: show the icon disabled, like Dismiss.
  if (fields.length === 0) {
    return (
      <Tooltip label="Sort" withArrow>
        <ActionIcon
          variant="light"
          size={40}
          c="var(--black)"
          aria-label="Sort"
          disabled
        >
          <PiSortDescending size={20} />
        </ActionIcon>
      </Tooltip>
    );
  }

  const tabDefault = defaultCandidateFilters(activeTab);
  const isDefaultSort =
    sortBy === tabDefault.sortBy && order === tabDefault.order;

  const setSortBy = (value: string) =>
    setCandidateFilters({ ...candidateFilters, sortBy: value });

  const setOrder = (value: string) =>
    setCandidateFilters({ ...candidateFilters, order: value });

  const resetSort = () =>
    setCandidateFilters({
      ...candidateFilters,
      sortBy: tabDefault.sortBy,
      order: tabDefault.order,
    });

  return (
    <Popover width={220} position="bottom-end" withArrow shadow="md">
      <Popover.Target>
        <Tooltip label="Sort" withArrow>
          <ActionIcon
            variant={isDefaultSort ? 'light' : 'filled'}
            color={isDefaultSort ? undefined : 'brandYellow'}
            size={40}
            c="var(--black)"
            aria-label="Sort"
          >
            {isAsc ? (
              <PiSortAscending size={20} />
            ) : (
              <PiSortDescending size={20} />
            )}
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="sm">
          <Radio.Group
            label="Sort by"
            value={sortBy ?? null}
            onChange={setSortBy}
          >
            <Stack gap="xs" mt="xs">
              {fields.map((field) => (
                <Radio
                  key={field.value}
                  value={field.value}
                  label={field.label}
                />
              ))}
            </Stack>
          </Radio.Group>

          <Divider />

          <SegmentedControl
            value={isAsc ? 'asc' : 'desc'}
            onChange={setOrder}
            data={[
              { value: 'asc', label: 'Asc' },
              { value: 'desc', label: 'Desc' },
            ]}
            fullWidth
            size="xs"
          />

          {!isDefaultSort && (
            <Anchor component="button" size="xs" c="dimmed" onClick={resetSort}>
              Reset to default
            </Anchor>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
